import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { api, ApiError, refreshAccessToken } from "@/lib/api";

/**
 * Auth state is server-driven — the JWT lives in an httpOnly cookie set by
 * /auth/login. The frontend never sees the token; we know the user is
 * authenticated when /api/accounts/me returns 200.
 *
 * This context holds:
 *   - `status`: "loading" | "authenticated" | "anonymous"
 *   - `userId`: parsed from /accounts/me when authed
 *   - actions: login / register / logout
 *
 * Components don't poll — they call useAuth() and either render the
 * protected UI (if authenticated) or get redirected by <ProtectedRoute>.
 */

type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  status: AuthStatus;
  userId: number | null;
  email: string | null;
  refresh: () => Promise<void>;
  login: (email: string, password: string, rememberDevice?: boolean) => Promise<LoginOutcome>;
  verifyTwoFactor: (otpCode: string) => Promise<void>;
  register: (fullName: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

interface MeResponse {
  id: number;
  user_id: number;
  status: string;
}

/** Login can finish outright, or (2FA on for this account) pause partway
 *  through — password was right, but there's no real session yet, only a
 *  short-lived temp_token cookie. The caller checks `requires2FA` and, if
 *  true, shows an OTP-entry step instead of navigating away. */
export type LoginOutcome = { requires2FA: boolean };

interface LoginApiResponse {
  success?: boolean;
  message?: string;
  requires2FA?: boolean;
}

const HAS_SESSION_KEY = "hosterdash_has_session";

function hasSessionHint(): boolean {
  try {
    return localStorage.getItem(HAS_SESSION_KEY) === "true";
  } catch {
    return false;
  }
}

function setSessionHint(hasSession: boolean) {
  try {
    if (hasSession) {
      localStorage.setItem(HAS_SESSION_KEY, "true");
    } else {
      localStorage.removeItem(HAS_SESSION_KEY);
    }
  } catch {
    // Ignore storage quota / privacy mode errors
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = React.useState<AuthStatus>(() =>
    hasSessionHint() ? "loading" : "anonymous"
  );
  const [userId, setUserId] = React.useState<number | null>(null);
  const [email, setEmail] = React.useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // ── Proactive token refresh ───────────────────────────────────────
  // The access_token cookie is short-lived (minutes). Rather than wait for a
  // 401, we refresh ~30s before it expires, then reschedule off the new expiry
  // the refresh endpoint returns. The reactive 401-retry in lib/api is the
  // safety net (e.g. after the laptop sleeps and the timer is late).
  const refreshTimer = React.useRef<number | undefined>(undefined);

  const clearRefreshTimer = React.useCallback(() => {
    if (refreshTimer.current !== undefined) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = undefined;
    }
  }, []);

  // Refresh roughly every 2 minutes — comfortably inside the 3-minute access
  // token. Routed through the shared lock in lib/api so it can never race the
  // reactive 401-retry (both share one in-flight refresh).
  const scheduleProactiveRefresh = React.useCallback(() => {
    clearRefreshTimer();
    const INTERVAL_MS = 120_000;
    refreshTimer.current = window.setTimeout(async () => {
      const ok = await refreshAccessToken();
      if (ok) {
        scheduleProactiveRefresh();
      } else {
        // Refresh token is gone/expired — the session is genuinely over.
        clearRefreshTimer();
        setSessionHint(false);
        setUserId(null);
        setStatus("anonymous");
      }
    }, INTERVAL_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearRefreshTimer]);

  // Boot — hit /accounts/me once at startup if user was previously authed. 401 means anonymous.
  const refresh = React.useCallback(async () => {
    try {
      const me = await api.get<MeResponse>("/api/accounts/me");
      setSessionHint(true);
      setUserId(me.user_id);
      setStatus("authenticated");
      scheduleProactiveRefresh();
    } catch (err) {
      setSessionHint(false);
      if (err instanceof ApiError && err.status === 401) {
        setUserId(null);
        setStatus("anonymous");
      } else {
        // Network / 5xx — surface as anonymous so the UI still renders
        // the login page; the user can retry from there.
        setUserId(null);
        setStatus("anonymous");
      }
    }
  }, [scheduleProactiveRefresh]);

  React.useEffect(() => {
    if (hasSessionHint()) {
      void refresh();
    }
  }, [refresh]);

  const login = React.useCallback(
    async (emailArg: string, password: string, rememberDevice = false): Promise<LoginOutcome> => {
      const result = await api.post<LoginApiResponse>(
        "/auth/login",
        { email: emailArg, password, rememberDevice }
      );
      // The backend returns 200 with success=false on bad credentials
      // (instead of 401) — surface that uniformly.
      if (result && result.success === false) {
        throw new ApiError(401, result, result.message ?? "Invalid credentials.");
      }
      // 2FA is on for this account — the password was correct, but the
      // backend only issued a short-lived temp_token cookie, not a real
      // session. Don't call refresh() yet (there's nothing to refresh into);
      // let the caller show an OTP-entry step and finish via verifyTwoFactor.
      if (result && result.requires2FA) {
        return { requires2FA: true };
      }
      setSessionHint(true);
      setEmail(emailArg);
      await refresh();
      return { requires2FA: false };
    },
    [refresh]
  );

  const verifyTwoFactor = React.useCallback(
    async (otpCode: string) => {
      // Completes the login-challenge started above. Authenticated purely by
      // the temp_token cookie set during /auth/login — nothing else to pass.
      // On success the backend sets the real access/refresh cookies, same as
      // a normal login, so we can just refresh() to pick up the session.
      await api.post("/auth/2fa/verify", { otpCode });
      setSessionHint(true);
      await refresh();
    },
    [refresh]
  );

  const register = React.useCallback(
    async (fullName: string, emailArg: string, password: string) => {
      // RegisterUserCommand fields (HosterDash.Application/Features/Authentication):
      //   FullName, Email, Password, Role (UserRoles enum — Customer = 3)
      //
      // ASP.NET model-bind is case-insensitive, so PascalCase / camelCase
      // both work on the wire. Role is a required FluentValidation rule
      // even though customers should always self-sign-up as Customer.
      await api.post("/auth/register", {
        fullName,
        email: emailArg,
        password,
        role: 3 // UserRoles.Customer
      });
      // Registration doesn't auto-login on this backend; the page navigates
      // to /login after a successful response.
    },
    []
  );

  const logout = React.useCallback(async () => {
    clearRefreshTimer();
    setSessionHint(false);
    try {
      await api.delete("/auth/logout");
    } catch {
      // best-effort — even if the call fails we still clear local state.
    }
    setUserId(null);
    setEmail(null);
    setStatus("anonymous");
    queryClient.clear();
    navigate("/login", { replace: true });
  }, [navigate, queryClient, clearRefreshTimer]);

  // Stop the proactive timer when the provider unmounts.
  React.useEffect(() => clearRefreshTimer, [clearRefreshTimer]);

  const value = React.useMemo<AuthContextValue>(
    () => ({ status, userId, email, refresh, login, verifyTwoFactor, register, logout }),
    [status, userId, email, refresh, login, verifyTwoFactor, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
